import { dateKeyFromString, isDateInRange } from './reportDates';
import { isAutomotiveBusiness, isBrickFactoryUser, isPharmacyUser } from './businessType';
import { hasTenantModule } from './tenantModules';

export function saleProfit(sale) {
  const p = parseFloat(sale?.profit);
  if (!Number.isNaN(p)) return p;
  const amount = parseFloat(sale?.amount);
  const cost = parseFloat(sale?.cost);
  if (!Number.isNaN(amount) && !Number.isNaN(cost)) return amount - cost;
  return 0;
}

function orderDateKey(order) {
  return dateKeyFromString(order?.created_at || order?.createdAt);
}

function expenseDateKey(expense) {
  return String(expense?.expenseDate || expense?.expense_date || '').slice(0, 10);
}

const PURCHASE_SKIP_STATUSES = new Set(['rejected', 'cancelled']);

export function buildFinancialReport({
  sales = [],
  expenses = [],
  orders = [],
  fromDate,
  toDate,
  user,
}) {
  const inRange = (dateKey) => isDateInRange(dateKey, fromDate, toDate);

  const salesRows = (sales || []).filter((s) => inRange(dateKeyFromString(s.date)));
  const expenseRows = (expenses || []).filter((e) => inRange(expenseDateKey(e)));
  const purchaseRows = (orders || []).filter((o) => {
    const st = String(o?.status || '').toLowerCase();
    if (PURCHASE_SKIP_STATUSES.has(st)) return false;
    return inRange(orderDateKey(o));
  });

  const salesRevenue = salesRows.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
  const salesCost = salesRows.reduce((sum, s) => sum + (Number(s.cost) || 0), 0);
  const salesProfit = salesRows.reduce((sum, s) => sum + saleProfit(s), 0);

  const purchasesTotal = purchaseRows.reduce(
    (sum, o) => sum + (Number(o.estimated_total_tzs ?? o.estimatedTotalTzs) || 0),
    0
  );

  const expensesTotal = expenseRows.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  const expensesBoss = expenseRows
    .filter((e) => String(e.category) === 'boss')
    .reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  const expensesStaff = expenseRows
    .filter((e) => String(e.category) === 'staff')
    .reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  const expensesOffice = expenseRows
    .filter((e) => String(e.category) === 'office')
    .reduce((sum, e) => sum + (Number(e.amount) || 0), 0);

  const netProfit = salesProfit - expensesTotal;

  return {
    salesRows,
    expenseRows,
    purchaseRows,
    salesRevenue,
    salesCost,
    salesProfit,
    salesCount: salesRows.length,
    purchasesTotal,
    purchasesCount: purchaseRows.length,
    expensesTotal,
    expensesBoss,
    expensesStaff,
    expensesOffice,
    netProfit,
  };
}

export function financialReportLabels(user) {
  if (isAutomotiveBusiness(user)) {
    return {
      title: 'Business financial report',
      subtitle: 'Mauzo ya spare parts, manunuzi, matumizi, na faida — kwa kipindi ulichochagua.',
      sales: 'Sales (POS)',
      salesHint: 'Revenue from spare parts sold',
      purchases: 'Purchases',
      purchasesHint: 'Purchase orders placed in this period',
      expenses: 'Expenses',
      expensesHint: 'Boss, staff & office spending',
      profit: 'Net profit',
      profitHint: 'Sales profit minus expenses',
      productCol: 'Spare part',
    };
  }
  if (isBrickFactoryUser(user)) {
    return {
      title: 'Factory financial report',
      subtitle: 'Sales, material purchases, expenses, and profit for the selected period.',
      sales: 'Sales (POS)',
      salesHint: 'Bricks, tiles & cement sold',
      purchases: 'Material orders',
      purchasesHint: 'Supplier orders in this period',
      expenses: 'Expenses',
      expensesHint: 'Operating costs',
      profit: 'Net profit',
      profitHint: 'Sales profit minus expenses',
      productCol: 'Product',
    };
  }
  if (isPharmacyUser(user)) {
    return {
      title: 'Pharmacy financial report',
      subtitle: 'Sales, purchases, expenses, and profit for the selected period.',
      sales: 'Sales (POS)',
      salesHint: 'Medicine & product revenue',
      purchases: 'Purchases',
      purchasesHint: 'Stock orders in this period',
      expenses: 'Expenses',
      expensesHint: 'Operating costs',
      profit: 'Net profit',
      profitHint: 'Sales profit minus expenses',
      productCol: 'Product',
    };
  }
  return {
    title: 'Business financial report',
    subtitle: 'Sales, purchases, expenses, and profit for the selected period.',
    sales: 'Sales (POS)',
    salesHint: 'Revenue from product sales',
    purchases: 'Purchases',
    purchasesHint: 'Purchase orders in this period',
    expenses: 'Expenses',
    expensesHint: 'Boss, staff & office spending',
    profit: 'Net profit',
    profitHint: 'Sales profit minus expenses',
    productCol: 'Product',
  };
}

export function showPrintingInReports(user) {
  return hasTenantModule(user, 'printing') && !isBrickFactoryUser(user) && !isAutomotiveBusiness(user);
}

export function formatTzs(value) {
  return `TZS ${Math.round(Number(value) || 0).toLocaleString()}`;
}
